import { useState } from "react";
import { publicApi } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const SERVICES = ["Exterior Painting", "Interior Painting", "Other"];
const PROPERTY = ["Residential", "Commercial"];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "Exterior Painting",
    property_type: "Residential",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await publicApi.post("/leads", form);
      setDone(true);
      toast.success("Quote request sent! We'll call you today.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className="border border-brand-border bg-brand-surface p-10 flex flex-col items-start gap-4"
      >
        <CheckCircle2 className="h-10 w-10 text-brand-blue" />
        <h3 className="font-display uppercase text-4xl leading-none">Quote requested.</h3>
        <p className="text-brand-muted text-sm max-w-sm">
          Thanks {form.name.split(" ")[0]} — we&apos;ve received your request and will
          reach out at <span className="text-white">{form.phone}</span> today.
        </p>
        <a
          href="tel:+13342019444"
          className="mt-2 text-brand-blue font-display text-2xl tracking-wide uppercase link-underline"
        >
          Or call 334-201-9444 →
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="relative border border-brand-border bg-brand-surface/80 backdrop-blur-xl p-6 md:p-8"
    >
      <div className="absolute -top-3 left-6 bg-brand-blue text-white text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-semibold">
        Same-Day · Free
      </div>

      <h3 className="font-display uppercase text-3xl md:text-4xl leading-none mt-3">
        Get your free <span className="text-brand-blue">quote</span>
      </h3>
      <p className="text-brand-muted text-sm mt-2 mb-6">
        Fill this out. We call you back <span className="text-white">today.</span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          testid="lead-name"
          label="Full name*"
          value={form.name}
          onChange={set("name")}
          placeholder="Jane Doe"
        />
        <Field
          testid="lead-phone"
          label="Phone*"
          value={form.phone}
          onChange={set("phone")}
          placeholder="(334) 555-0000"
          type="tel"
        />
        <Field
          testid="lead-email"
          label="Email (optional)"
          value={form.email}
          onChange={set("email")}
          placeholder="you@email.com"
          type="email"
          full
        />
        <SelectField
          testid="lead-service"
          label="Service"
          value={form.service}
          onChange={set("service")}
          options={SERVICES}
        />
        <SelectField
          testid="lead-property"
          label="Property"
          value={form.property_type}
          onChange={set("property_type")}
          options={PROPERTY}
        />
        <div className="sm:col-span-2">
          <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-2">
            Project details
          </label>
          <textarea
            data-testid="lead-message"
            value={form.message}
            onChange={set("message")}
            rows={3}
            placeholder="Tell us what you need painted or built..."
            className="w-full bg-brand-bg border border-brand-border focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none text-white placeholder:text-zinc-600 px-4 py-3 text-sm"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit-btn"
        className="mt-6 w-full bg-brand-blue hover:bg-brand-blueHover disabled:opacity-60 text-white font-display uppercase tracking-widest text-xl py-4 flex items-center justify-center gap-2 transition-colors"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {loading ? "Sending..." : "Request Free Quote"}
      </button>
      <p className="text-[10px] tracking-widest uppercase text-brand-muted text-center mt-3">
        No spam. No obligation. Just a fast answer.
      </p>
    </form>
  );
}

function Field({ label, testid, full, ...props }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        {...props}
        className="w-full bg-brand-bg border border-brand-border focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none text-white placeholder:text-zinc-600 px-4 py-3 text-sm"
      />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-brand-bg border border-brand-border focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none text-white px-4 py-3 text-sm appearance-none"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-brand-bg">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
